/**
 * Post-Checkout TRAY Builder
 *
 * Single-screen customization for AppyHour Gourmet Bites orders (SKU AHB-{M|L}CUST-TRAY).
 * The customer can swap among trays from the Gourmet Bites collection only —
 * no curation/dietary/theme choices.
 *
 * The shared bootstrap (in page-post-checkout-box-builder.liquid) calls /init
 * and stores the response in window.__bbInitData. If flowType === 'tray', this
 * class takes over and renders the picker UI.
 *
 * All DOM IDs are prefixed with `tray-` to avoid colliding with the existing
 * dietary-flow section (both sections render on the same URL).
 */

class PostCheckoutTrayBuilder {
  constructor(config) {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || '/apps/box-builder',
      orderId: this.getOrderIdFromUrl(),
      orderToken: this.getTokenFromUrl(),
      trayRedirectUrl: config.trayRedirectUrl || '/pages/box-customized?status=tray',
      texts: config.texts || {},
      // How long to wait for the page bootstrap's /init before sending our own
      initWaitMs: config.initWaitMs || 20000,
    };

    this.state = {
      // Session & order info (populated from /init)
      sessionId: null,
      orderNumber: null,
      itemCount: 6,
      boxSize: 'M',
      shipDate: null,
      shipWeek: null,
      boxSizePricing: null,

      // Items
      availableItems: [],
      collections: [],
      defaultItems: [], // [{ variantId, quantity }]
      selectedItems: [], // [{ variantId, productId, quantity, title }]

      // UI
      isLoading: false,
      isCompleting: false,
      error: null,
    };

    this.elements = {};
    this.init();
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  async init() {
    try {
      this.cacheElements();

      // Move footer to document.body so position:fixed escapes ancestor transforms
      if (this.elements.footer) {
        document.body.appendChild(this.elements.footer);
      }

      this.setupEventListeners();

      // Wait for the shared bootstrap's /init. Fetch it ourselves only if there is
      // no bootstrap or its request failed or stalled — never alongside it.
      let initData = await this.waitForBootstrapInit();
      if (!initData) {
        if (!this.config.orderId) {
          this.showError('No order ID provided. Please use the link from your order confirmation.');
          return;
        }
        this.showLoading(this.config.texts.loadingInit || 'Loading your order...');
        let url = `${this.config.apiBaseUrl}/init?order_id=${this.config.orderId}`;
        if (this.config.orderToken) {
          url += `&token=${encodeURIComponent(this.config.orderToken)}`;
        }
        initData = await this.apiCall(url);
        window.__bbInitData = initData;
      }

      // Backend error (e.g. BUNDLE_NOT_READY when Recharge hasn't written the
      // bundle yet, or Nest's own "Order not found", which has no success field).
      // The bootstrap caches error responses without revealing the tray section,
      // so this branch is reached only when the standard section is visible —
      // let its JS render the error message.
      if (!initData || initData.success !== true) {
        return;
      }

      // If this isn't a TRAY flow, the existing section's JS handles it — bail silently
      if (initData.flowType !== 'tray') {
        return;
      }

      // Confirmed tray flow — reveal the fixed footer (default-hidden in the Liquid)
      if (this.elements.footer) {
        this.elements.footer.style.display = '';
      }
      this.syncLockNote(true);

      this.applyInitData(initData);

      // Load items + defaults concurrently, then render
      await this.loadItemsAndDefaults();

      this.hideLoading();
      this.updateProgress();
    } catch (error) {
      console.error('[TrayBuilder] Init failed:', error);
      this.hideLoading();
      this.showError(error.message || 'Failed to initialize. Please refresh the page.', error && error.code);
    }
  }

  /**
   * The page's shared bootstrap (page-post-checkout-box-builder.liquid) sends the
   * one /init for this page load. Wait for its answer rather than sending our own:
   * simultaneous /init calls used to race and create duplicate sessions for one
   * order. Resolves to null when there is no bootstrap request, it failed, or it
   * has not answered within initWaitMs; the caller then sends /init itself.
   */
  async waitForBootstrapInit() {
    if (window.__bbInitData) return window.__bbInitData;
    if (!window.__bbInitPromise) return null;
    let timer;
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), this.config.initWaitMs);
    });
    try {
      return (await Promise.race([window.__bbInitPromise, timedOut])) || null;
    } finally {
      clearTimeout(timer);
    }
  }

  cacheElements() {
    this.elements = {
      container: document.querySelector('.post-checkout-tray-builder'),
      loadingOverlay: document.getElementById('tray-loading-overlay'),
      loadingMessage: document.querySelector('#tray-loading-overlay .loading-overlay__message'),
      errorState: document.getElementById('tray-error-state'),
      errorMessage: document.getElementById('tray-error-message'),
      orderNumber: document.getElementById('tray-order-number'),
      boxSizeLabel: document.getElementById('tray-box-size-label'),
      shipWeek: document.getElementById('tray-ship-week'),
      pickerTitle: document.getElementById('tray-picker-title'),
      pickerSubtitle: document.getElementById('tray-picker-subtitle'),
      selectedCount: document.getElementById('tray-selected-count'),
      maxItems: document.getElementById('tray-max-items'),
      productGrid: document.getElementById('tray-product-grid'),
      itemProgressFill: document.getElementById('tray-item-progress-fill'),
      btnSave: document.getElementById('tray-btn-save'),
      footer: document.getElementById('tray-builder-footer'),
      deadlineNote: document.getElementById('tray-customize-deadline'),
      lockNote: document.getElementById('tray-complete-lock-note'),
    };
  }

  getOrderIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('order_id') || params.get('orderId') || params.get('order');
  }

  getTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || params.get('key') || params.get('access_token');
  }

  applyInitData(data) {
    this.state.sessionId = data.session.sessionId;
    this.state.orderNumber = data.order?.orderNumber || null;
    this.state.itemCount = data.session.itemCount || 6;
    this.state.boxSize = data.session.boxSize || 'M';
    this.state.shipDate = data.session.shipDate || null;
    this.state.shipWeek = data.session.shipWeek || null;
    this.state.boxSizePricing = data.boxSizePricing || null;

    this.updateOrderInfoBar();
    this.updateMaxItemsDisplay();
    this.renderDeadlineNote(data.session.expiresAt);
  }

  updateOrderInfoBar() {
    if (this.elements.orderNumber) {
      this.elements.orderNumber.textContent = this.state.orderNumber || '—';
    }
    if (this.elements.boxSizeLabel) {
      this.elements.boxSizeLabel.textContent = `${this.state.itemCount} Items`;
    }
    if (this.elements.shipWeek) {
      this.elements.shipWeek.textContent =
        this.state.shipWeek || this.formatShipDate(this.state.shipDate) || 'TBD';
    }
  }

  updateMaxItemsDisplay() {
    if (this.elements.maxItems) {
      this.elements.maxItems.textContent = this.state.itemCount;
    }
  }

  formatShipDate(shipDate) {
    if (!shipDate) return null;
    try {
      const date = new Date(shipDate);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return shipDate;
    }
  }

  // ============================================================
  // API CALLS
  // ============================================================

  async apiCall(url, options = {}) {
    const maxRetries = 3;
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new TrayAPIError(
            data.error || 'API_ERROR',
            data.message || 'Request failed',
            response.status,
          );
        }
        return data;
      } catch (error) {
        lastError = error;
        if (error.status >= 400 && error.status < 500) break;
        if (attempt < maxRetries) {
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
    }
    throw lastError;
  }

  async loadItemsAndDefaults() {
    this.showLoading(this.config.texts.loadingItems || 'Loading products...');

    const [itemsData, defaultsData] = await Promise.all([
      this.apiCall(`${this.config.apiBaseUrl}/tray/items?sessionId=${this.state.sessionId}`),
      this.apiCall(`${this.config.apiBaseUrl}/tray/default-items?sessionId=${this.state.sessionId}`),
    ]);

    this.state.availableItems = itemsData.items || [];
    this.state.collections = itemsData.collections || [];
    this.state.defaultItems = defaultsData.defaultItems || [];

    this.renderProductGrid(this.state.availableItems, this.state.collections);
    this.applyDefaultSelections(this.state.defaultItems);

    this.hideLoading();
  }

  // ============================================================
  // RENDERING
  // ============================================================

  renderProductGrid(items, collections) {
    const grid = this.elements.productGrid;
    if (!grid) {
      console.warn('[TrayBuilder] Product grid element not found');
      return;
    }

    if (!items || items.length === 0) {
      grid.innerHTML = '<div class="product-grid__empty">No products available right now. Please contact support.</div>';
      return;
    }

    // Build collection list (use server-provided collections, fall back to deriving from items)
    const uniqueCollections =
      collections && collections.length > 0
        ? collections
        : [
            ...new Map(
              items.map((i) => [i.collectionId, { id: i.collectionId, name: i.collectionName }]),
            ).values(),
          ];

    // Group items by collection
    const grouped = new Map();
    for (const item of items) {
      const cid = item.collectionId || 'uncategorized';
      if (!grouped.has(cid)) grouped.set(cid, []);
      grouped.get(cid).push(item);
    }

    let html = '';
    for (const collection of uniqueCollections) {
      const cid = collection.id || collection;
      const cname = collection.name || collection;
      const cItems = grouped.get(cid) || grouped.get(String(cid));
      if (!cItems || cItems.length === 0) continue;

      html += `
        <div class="collection-group" data-collection-id="${cid}">
          <h3 class="collection-group__title">${cname}</h3>
          <div class="collection-group__grid">
            ${cItems.map((p) => this.renderProductCard(p)).join('')}
          </div>
        </div>
      `;
    }

    if (!html) {
      // Fall back to a flat grid if grouping produced nothing
      html = `
        <div class="collection-group">
          <div class="collection-group__grid">
            ${items.map((p) => this.renderProductCard(p)).join('')}
          </div>
        </div>
      `;
    }

    grid.innerHTML = html;

    // Reset state since we re-rendered
    this.state.selectedItems = [];

    this.setupProductCardListeners();
    this.updateProgress();
  }

  renderProductCard(product) {
    const inStock = product.inStock !== false;
    const maxQty = 3;
    const actionsHtml = inStock
      ? `
        <div class="product-card__actions">
          <button type="button" class="product-card__add-btn">+ Add</button>
          <div class="product-card__quantity" style="display: none;">
            <button type="button" class="qty-btn decrease" disabled>−</button>
            <input type="number" value="0" min="0" max="${maxQty}" readonly>
            <button type="button" class="qty-btn increase">+</button>
          </div>
        </div>
      `
      : `
        <div class="product-card__actions">
          <span class="product-card__unavailable-text">Unavailable</span>
        </div>
      `;

    const hasInfo = product.description || product.customerPortalDescription;
    const infoIconHtml = hasInfo
      ? `<button type="button" class="product-card__info-icon" aria-label="Product info">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="M50,7A43,43,0,1,0,93,50,43,43,0,0,0,50,7Zm6.93,68.29a6.68,6.68,0,0,1-6.88,6.45,7.1,7.1,0,0,1-4.86-1.89,6.25,6.25,0,0,1-2-4.56V45.72a6.67,6.67,0,0,1,6.87-6.44,7.14,7.14,0,0,1,4.87,1.88,6.25,6.25,0,0,1,2,4.56ZM50,33.05a7.4,7.4,0,1,1,7.39-7.4A7.39,7.39,0,0,1,50,33.05Z"/></svg>
        </button>`
      : '';

    let cardDescHtml = '';
    if (product.customizerCardDescription) {
      try {
        cardDescHtml = this.renderRichText(JSON.parse(product.customizerCardDescription));
      } catch { /* malformed JSON — skip silently */ }
    }

    return `
      <div class="product-card ${!inStock ? 'product-card--unavailable' : ''} ${product.pcHide ? 'product-card--pc-hidden' : ''}"
           data-product-id="${product.productId}"
           data-variant-id="${product.variantId}"
           data-collection-id="${product.collectionId || ''}"
           data-pc-hide="${product.pcHide ? 'true' : 'false'}"
           data-max-qty="${maxQty}">
        <div class="product-card__image">
          ${product.imageUrl
            ? `<img src="${product.imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
            : '<div class="product-card__image-placeholder"></div>'}
          ${!inStock ? '<div class="product-card__unavailable-badge">Out of Stock</div>' : ''}
          ${infoIconHtml}
        </div>
        <div class="product-card__info">
          <h4 class="product-card__title">${this.escapeHtml(product.title)}</h4>
          ${cardDescHtml ? `<div class="product-card__description">${cardDescHtml}</div>` : ''}
        </div>
        ${actionsHtml}
      </div>
    `;
  }

  setupProductCardListeners() {
    const grid = this.elements.productGrid;
    if (!grid) return;

    grid.querySelectorAll('.product-card').forEach((card) => {
      const addBtn = card.querySelector('.product-card__add-btn');
      const qtyContainer = card.querySelector('.product-card__quantity');
      const increaseBtn = card.querySelector('.increase');
      const decreaseBtn = card.querySelector('.decrease');

      if (addBtn) {
        addBtn.addEventListener('click', () => {
          addBtn.style.display = 'none';
          if (qtyContainer) qtyContainer.style.display = 'flex';
          this.adjustQuantity(card, 1);
        });
      }
      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => this.adjustQuantity(card, 1));
      }
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => this.adjustQuantity(card, -1));
      }
    });

    // Info popup
    grid.querySelectorAll('.product-card__info-icon').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = btn.closest('.product-card');
        const variantId = card.getAttribute('data-variant-id');
        const product = this.state.availableItems.find((i) => i.variantId === variantId);
        if (product) this.createProductInfoPopup(product);
      });
    });
  }

  /**
   * Pre-select default items returned by /tray/default-items.
   * Some defaults may have been dropped (out-of-stock); the picker just leaves
   * those slots empty and waits for the customer to fill them in.
   */
  applyDefaultSelections(defaults) {
    if (!defaults || defaults.length === 0) {
      this.updateProgress();
      return;
    }

    // Reveal pc_hide cards that are part of the defaults
    const defaultVariantIds = new Set(defaults.map((d) => String(d.variantId)));
    document.querySelectorAll('.product-card--pc-hidden').forEach((card) => {
      if (defaultVariantIds.has(card.getAttribute('data-variant-id'))) {
        card.classList.remove('product-card--pc-hidden');
        card.classList.add('product-card--pc-revealed');
      }
    });

    // Apply selections to matching cards
    const selected = [];
    for (const def of defaults) {
      const card = document.querySelector(`.product-card[data-variant-id="${def.variantId}"]`);
      if (!card || card.classList.contains('product-card--unavailable')) continue;

      const input = card.querySelector('input[type="number"]');
      const addBtn = card.querySelector('.product-card__add-btn');
      const qtyContainer = card.querySelector('.product-card__quantity');
      const decreaseBtn = card.querySelector('.decrease');

      if (input) input.value = def.quantity;
      card.classList.add('active');
      if (addBtn) addBtn.style.display = 'none';
      if (qtyContainer) qtyContainer.style.display = 'flex';
      if (decreaseBtn) decreaseBtn.disabled = def.quantity === 0;

      selected.push({
        productId: card.dataset.productId,
        variantId: String(def.variantId),
        quantity: def.quantity,
        title: card.querySelector('.product-card__title')?.textContent || '',
      });
    }

    this.state.selectedItems = selected;
    this.updateProgress();
  }

  // ============================================================
  // QUANTITY HANDLING
  // ============================================================

  adjustQuantity(card, delta) {
    const input = card.querySelector('input[type="number"]');
    const addBtn = card.querySelector('.product-card__add-btn');
    const qtyContainer = card.querySelector('.product-card__quantity');
    if (!input) return;

    const currentValue = parseInt(input.value, 10) || 0;
    const maxValue = parseInt(input.max, 10) || 3;
    const newValue = Math.max(0, Math.min(maxValue, currentValue + delta));

    const totalSelected = this.getTotalSelectedItems();
    if (delta > 0 && totalSelected >= this.state.itemCount) {
      this.showToast(`Maximum ${this.state.itemCount} items allowed`, 'warning');
      return;
    }

    input.value = newValue;
    const decreaseBtn = card.querySelector('.decrease');
    const increaseBtn = card.querySelector('.increase');
    if (decreaseBtn) decreaseBtn.disabled = newValue === 0;
    if (increaseBtn) {
      increaseBtn.disabled = newValue >= maxValue || totalSelected >= this.state.itemCount - 1;
    }

    if (newValue === 0) {
      if (addBtn) addBtn.style.display = 'block';
      if (qtyContainer) qtyContainer.style.display = 'none';
      card.classList.remove('active');
    } else {
      card.classList.add('active');
    }

    this.syncSelectedItemsFromDom();
    this.updateProgress();
    this.saveToStorage();
  }

  syncSelectedItemsFromDom() {
    this.state.selectedItems = [];
    document.querySelectorAll('.post-checkout-tray-builder .product-card.active').forEach((card) => {
      const qty = parseInt(card.querySelector('input[type="number"]')?.value, 10) || 0;
      if (qty > 0) {
        this.state.selectedItems.push({
          productId: card.dataset.productId,
          variantId: card.dataset.variantId,
          quantity: qty,
          title: card.querySelector('.product-card__title')?.textContent || '',
        });
      }
    });
  }

  getTotalSelectedItems() {
    return this.state.selectedItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
  }

  updateProgress() {
    const total = this.getTotalSelectedItems();
    if (this.elements.selectedCount) this.elements.selectedCount.textContent = total;
    if (this.elements.maxItems) this.elements.maxItems.textContent = this.state.itemCount;

    const fillPct = Math.min(100, (total / this.state.itemCount) * 100);
    if (this.elements.itemProgressFill) {
      this.elements.itemProgressFill.style.width = `${fillPct}%`;
      this.elements.itemProgressFill.classList.toggle('complete', total >= this.state.itemCount);
    }

    // Disable cards/buttons when full
    if (total >= this.state.itemCount) {
      document.querySelectorAll('.post-checkout-tray-builder .product-card:not(.active)').forEach((card) => {
        card.classList.add('disabled');
      });
      document.querySelectorAll('.post-checkout-tray-builder .product-card .increase').forEach((btn) => {
        btn.disabled = true;
      });
    } else {
      document.querySelectorAll('.post-checkout-tray-builder .product-card.disabled').forEach((card) => {
        card.classList.remove('disabled');
      });
      document.querySelectorAll('.post-checkout-tray-builder .product-card.active .increase').forEach((btn) => {
        const card = btn.closest('.product-card');
        const input = card.querySelector('input[type="number"]');
        const v = parseInt(input?.value, 10) || 0;
        const maxV = parseInt(input?.max, 10) || 3;
        btn.disabled = v >= maxV;
      });
    }

    this.updateSaveButton(total);
  }

  updateSaveButton(total) {
    const btn = this.elements.btnSave;
    if (!btn) return;

    if (total >= this.state.itemCount) {
      btn.disabled = false;
      btn.textContent = btn.dataset.completeText || 'Save my Box';
    } else {
      btn.disabled = true;
      const template = btn.dataset.incompleteText || 'Select %count% more items';
      const remaining = this.state.itemCount - total;
      btn.textContent = template.replace('%count%', String(remaining));
    }
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  setupEventListeners() {
    if (this.elements.btnSave) {
      this.elements.btnSave.addEventListener('click', () => this.handleSave());
    }

    // The lock-in note can wrap on narrow screens; keep the lifted offsets in step
    window.addEventListener('resize', () => {
      if (this.elements.lockNote && !this.elements.lockNote.classList.contains('hidden')) {
        this.syncLockNote(true);
      }
    });
  }

  async handleSave() {
    if (this.state.isLoading || this.state.isCompleting) return;

    const totalSelected = this.getTotalSelectedItems();
    if (totalSelected !== this.state.itemCount) {
      this.showToast(
        `Please select exactly ${this.state.itemCount} items (${totalSelected} selected)`,
        'error',
      );
      return;
    }

    this.state.isCompleting = true;
    if (this.elements.btnSave) this.elements.btnSave.disabled = true;
    this.showLoading(this.config.texts.completingBox || 'Saving your box...');

    try {
      // 1. Save items
      await this.apiCall(`${this.config.apiBaseUrl}/tray/items`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId: this.state.sessionId,
          items: this.state.selectedItems.map((i) => ({
            variantId: i.variantId,
            quantity: i.quantity,
          })),
        }),
      });

      // 2. Complete session
      const completeData = await this.apiCall(`${this.config.apiBaseUrl}/tray/complete`, {
        method: 'POST',
        body: JSON.stringify({ sessionId: this.state.sessionId }),
      });

      this.hideLoading();
      this.clearStorage();

      const redirectUrl =
        completeData.redirectUrl || this.config.trayRedirectUrl || '/pages/box-customized?status=tray';
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[TrayBuilder] Save failed:', error);
      this.state.isCompleting = false;
      // The window closed, or the box was completed elsewhere, while this page
      // was open. Nothing more can be saved, so explain instead of a toast.
      if (this.refusalCopy(error.code)) {
        this.showError(error.message, error.code);
        return;
      }
      if (this.elements.btnSave) this.elements.btnSave.disabled = false;
      this.hideLoading();
      this.showToast(error.message || 'Something went wrong. Please try again.', 'error');
    }
  }

  // ============================================================
  // PRODUCT INFO POPUP
  // ============================================================

  createProductInfoPopup(product) {
    const existing = document.querySelector('.product-info-popup');
    if (existing) existing.remove();

    let descriptionHtml = '';
    if (product.description) {
      descriptionHtml = product.description;
    } else if (product.customerPortalDescription) {
      try {
        const richText = JSON.parse(product.customerPortalDescription);
        descriptionHtml = this.renderRichText(richText);
      } catch {
        descriptionHtml = product.customerPortalDescription;
      }
    }

    const popup = document.createElement('div');
    popup.className = 'product-info-popup';
    popup.innerHTML = `
      <div class="product-info-popup-content">
        <button class="product-info-popup-close" aria-label="Close">&times;</button>
        <h2>${this.escapeHtml(product.title)}</h2>
        <div class="product-info-popup-columns">
          ${product.imageUrl
            ? `<div class="product-info-popup-image"><img src="${product.imageUrl}" alt="${this.escapeHtml(product.title)}"></div>`
            : ''}
          <div class="product-info-popup-description">${descriptionHtml}</div>
        </div>
      </div>
    `;
    document.body.appendChild(popup);
    document.body.classList.add('popup-open');
    document.documentElement.classList.add('html-popup-open');

    const close = () => {
      popup.remove();
      document.body.classList.remove('popup-open');
      document.documentElement.classList.remove('html-popup-open');
      document.removeEventListener('keydown', escHandler);
    };
    popup.querySelector('.product-info-popup-close').addEventListener('click', close);
    popup.addEventListener('click', (e) => {
      if (e.target === popup) close();
    });
    const escHandler = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', escHandler);
  }

  renderRichText(node) {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (node.type === 'text') {
      let text = (node.value || '').replace(/\n/g, '<br>');
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }
    const children = (node.children || []).map((child) => this.renderRichText(child)).join('');
    switch (node.type) {
      case 'root':
        return children;
      case 'paragraph':
        return `<p>${children}</p>`;
      case 'heading':
        return `<h${node.level || 3}>${children}</h${node.level || 3}>`;
      case 'list': {
        const tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${children}</${tag}>`;
      }
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link':
        return `<a href="${node.url || '#'}" target="_blank" rel="noopener">${children}</a>`;
      default:
        return children;
    }
  }

  // ============================================================
  // SESSION PERSISTENCE
  // ============================================================

  saveToStorage() {
    try {
      sessionStorage.setItem(
        'trayBuilderState',
        JSON.stringify({
          sessionId: this.state.sessionId,
          selectedItems: this.state.selectedItems,
          itemCount: this.state.itemCount,
          lastSavedAt: new Date().toISOString(),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  restoreFromStorage() {
    try {
      const saved = sessionStorage.getItem('trayBuilderState');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const ageHours = (Date.now() - new Date(parsed.lastSavedAt).getTime()) / (1000 * 60 * 60);
      if (ageHours >= 4) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  clearStorage() {
    try {
      sessionStorage.removeItem('trayBuilderState');
    } catch {
      /* ignore */
    }
  }

  // ============================================================
  // UI HELPERS
  // ============================================================

  showLoading(message = 'Loading...') {
    this.state.isLoading = true;
    if (this.elements.loadingOverlay) this.elements.loadingOverlay.classList.add('is-visible');
    if (this.elements.loadingMessage) this.elements.loadingMessage.textContent = message;
  }

  hideLoading() {
    this.state.isLoading = false;
    if (this.elements.loadingOverlay) this.elements.loadingOverlay.classList.remove('is-visible');
  }

  showError(message, code) {
    this.state.error = message;
    this.hideLoading();
    const refusal = this.refusalCopy(code);
    if (this.elements.errorState) {
      this.elements.errorState.classList.remove('hidden');
      if (refusal) {
        const titleEl = this.elements.errorState.querySelector('.error-state__title');
        const iconEl = this.elements.errorState.querySelector('.error-state__icon');
        if (titleEl) titleEl.textContent = refusal.title;
        if (iconEl) iconEl.textContent = refusal.icon;
      }
    }
    if (this.elements.errorMessage) this.elements.errorMessage.textContent = refusal ? refusal.message : message;
    // Nothing on the page can be saved any more: hide the picker, the notes and the footer
    const picker = document.querySelector('.post-checkout-step--tray');
    if (picker && refusal) picker.classList.add('hidden');
    if (this.elements.deadlineNote) this.elements.deadlineNote.classList.add('hidden');
    this.syncLockNote(false);
    if (this.elements.footer) this.elements.footer.style.display = 'none';
  }

  /**
   * Plain-language screen for the two refusals a returning customer can get
   * from the backend, or null for any other error. The copy comes from the
   * Post-Checkout Box Builder section (window.postCheckoutConfig.texts); these
   * strings are the fallbacks.
   */
  refusalCopy(code) {
    const texts = this.config.texts;
    if (code === 'CUSTOMIZE_WINDOW_CLOSED') {
      return {
        icon: '⏰',
        title: texts.windowClosedTitle || 'Your customization window has closed',
        message: texts.windowClosedMessage || "The time to customize this order has passed, so your box will ship with the items already in your order. Need a change? Contact our support team and we'll help.",
      };
    }
    if (code === 'ALREADY_CUSTOMIZED') {
      return {
        icon: '✅',
        title: texts.alreadyCustomizedTitle || 'Your box is already customized',
        message: texts.alreadyCustomizedMessage || "You've already completed your box, so your picks are locked in for this order. Need a change? Contact our support team and we'll help.",
      };
    }
    return null;
  }

  /**
   * Fill the customize-by note from /init's expiresAt, shown in the customer's
   * own time zone. The date is added only when the deadline isn't today
   * (e.g. a link support has extended). Stays hidden if the deadline is
   * missing or already past, or the note is blank in the theme editor.
   */
  renderDeadlineNote(expiresAt) {
    const el = this.elements.deadlineNote;
    const template = el ? el.dataset.template || '' : '';
    const deadline = this.formatDeadline(expiresAt);
    if (!el || !template || !deadline) return;

    el.textContent = '';
    template.split('%time%').forEach((part, i) => {
      if (i > 0) {
        const strong = document.createElement('strong');
        strong.textContent = deadline;
        el.appendChild(strong);
      }
      el.appendChild(document.createTextNode(part));
    });
    el.classList.remove('hidden');
  }

  formatDeadline(expiresAt) {
    if (!expiresAt) return null;
    const deadline = new Date(expiresAt);
    if (isNaN(deadline.getTime()) || deadline.getTime() <= Date.now()) return null;
    const time = deadline.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    if (deadline.toDateString() === new Date().toDateString()) return time;
    const day = deadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return `${day}, ${time}`;
  }

  /**
   * Show or hide the lock-in note above the Save button. The note makes the
   * fixed footer taller, so the floating item counter and toasts are lifted by
   * the footer's measured height (--pc-footer-height) while it shows.
   */
  syncLockNote(visible) {
    const { lockNote, footer } = this.elements;
    if (!lockNote) return;
    lockNote.classList.toggle('hidden', !visible);
    document.body.classList.toggle('pc-footer-note-visible', visible);
    if (visible && footer) {
      document.documentElement.style.setProperty('--pc-footer-height', `${footer.offsetHeight}px`);
    }
  }

  showToast(message, type = 'info') {
    document.querySelectorAll('.toast-notification').forEach((t) => t.remove());
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-notification--${type}`;
    toast.innerHTML = `
      <span class="toast-notification__message">${this.escapeHtml(message)}</span>
      <button type="button" class="toast-notification__close" onclick="this.parentElement.remove()">×</button>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('toast-notification--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

class TrayAPIError extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = 'TrayAPIError';
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function () {
  const trayContainer = document.querySelector('.post-checkout-tray-builder');
  if (!trayContainer) return;

  const config = window.postCheckoutConfig || {
    apiBaseUrl: trayContainer.dataset.apiUrl || '/apps/box-builder',
  };
  // Only instantiate when the bootstrap (or independent fetch in init()) confirms
  // flowType === 'tray'. The class itself bails silently for other flows.
  window.postCheckoutTrayBuilder = new PostCheckoutTrayBuilder(config);
});
